import React, { useState, useEffect, useReducer, useContext } from "react";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";

import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import {
  Telegram,
  AddCircle,
  Build,
  ContentCopy,
  DevicesFold,
  MoreVert,
  Edit,
  Delete,
  PlayArrow,
  Pause,
  WhatsApp,
  LocalOffer,
  Facebook,
  Instagram,
  Business,
  FilterList,
  ExpandMore,
  ChevronRight,
  Settings,
} from "@mui/icons-material";

import {
  CircularProgress,
  Stack,
  Grid,
  Box,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Container,
  Fab,
  useScrollTrigger,
  Slide,
  AppBar,
  Toolbar,
  Divider,
  Avatar,
  Collapse,
} from "@mui/material";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactModal from "../../components/ContactModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import FlowSetupModal from "../../components/FlowSetupModal";

import { i18n } from "../../translate/i18n";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import PageHelpGuide from "../../components/PageHelpGuide";
import { FLOW_BUILDER_HELP_DATA } from "../../constants/pageHelp/flowBuilderHelp";
import { getStandardButtonStyles } from "../../styles/standardButtons";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";
import NewTicketModal from "../../components/NewTicketModal";
import { SocketContext } from "../../context/Socket/SocketContext";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTS") {
    const contacts = action.payload;
    const newContacts = [];

    contacts.forEach((contact) => {
      const contactIndex = state.findIndex((c) => c.id === contact.id);
      if (contactIndex !== -1) {
        state[contactIndex] = contact;
      } else {
        newContacts.push(contact);
      }
    });

    return [...state, ...newContacts];
  }

  if (action.type === "UPDATE_CONTACTS") {
    const contact = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contact.id);

    if (contactIndex !== -1) {
      state[contactIndex] = contact;
      return [...state];
    } else {
      return [contact, ...state];
    }
  }

  if (action.type === "DELETE_CONTACT") {
    const contactId = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contactId);
    if (contactIndex !== -1) {
      state.splice(contactIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  ...getStandardButtonStyles(theme),
  rowItem: {
    cursor: "pointer",
    padding: "8px",
    backgroundColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.03)" : "rgba(0, 0, 0, 0.02)",
    borderRadius: 8,
    marginTop: 4,
    marginBottom: 4,
    border: `1px solid ${theme.palette.divider}`,
    transition: "background-color 0.2s",
    "&:hover": {
      backgroundColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.05)",
    },
  },
  tableHeader: {
    fontWeight: 'bold',
    backgroundColor: "#1e1e1e",
    color: "#fff",
    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
    position: "sticky",
    top: 0,
    zIndex: 1,
    borderTopLeftRadius: theme.shape.borderRadius,
    borderTopRightRadius: theme.shape.borderRadius,
  },

  root: {
    backgroundColor: theme.palette.background.default,
    minHeight: '100vh',
  },
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    margin: '0 auto',
    padding: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1),
    },
  },

  mainPaper: {
    flex: 1,
    height: 0,
    minHeight: 0,
    padding: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: 16,
    backgroundColor: theme.palette.type === 'dark' ? '#2A2D32 !important' : theme.palette.background.paper,
  },
  scrollArea: {
    flex: 1,
    height: 0,
    minHeight: 0,
    boxSizing: "border-box",
    overflowY: "auto",
    paddingRight: theme.spacing(1),
    ...theme.scrollbarStyles,
  },

  header: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
    borderRadius: 16,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    border: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderRadius: 12,
    },
  },
  title: {
    fontSize: '1.75rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
    margin: 0,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.5rem',
    },
  },
  searchContainer: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 16,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    boxShadow: theme.shadows[1],
    border: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.5),
      marginBottom: theme.spacing(2),
      borderRadius: 12,
    },
  },
  searchField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.type === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50],
      borderRadius: 12,
      '& fieldset': {
        border: 'none',
      },
      '&:hover': {
        backgroundColor: theme.palette.type === 'dark' ? theme.palette.grey[700] : theme.palette.grey[100],
      },
      '&.Mui-focused': {
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.palette.type === 'dark'
          ? '0 0 0 3px rgba(144, 202, 249, 0.16)'
          : '0 0 0 3px rgba(25, 118, 210, 0.12)',
      },
    },
    '& .MuiInputBase-input': {
      color: theme.palette.text.primary,
      fontSize: '0.95rem',
    },
  },
  flowCard: {
    backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.02)' : theme.palette.background.paper,
    borderRadius: 16,
    marginBottom: theme.spacing(2),
    border: '1px solid transparent !important', // Override global neon to avoid nesting
    boxShadow: 'none !important', // Override global neon shadow to avoid nesting
    borderBottom: theme.palette.type === 'dark' ? '1px solid rgba(255,255,255,0.05) !important' : `1px solid ${theme.palette.divider} !important`,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-2px)',
      backgroundColor: theme.palette.type === 'dark' ? 'rgba(0, 229, 255, 0.05)' : theme.palette.action.hover,
      border: theme.palette.type === 'dark' ? '1px solid rgba(0, 229, 255, 0.3) !important' : `1px solid ${theme.palette.primary.light} !important`,
      boxShadow: theme.palette.type === 'dark' ? '0 4px 12px rgba(0, 229, 255, 0.1) !important' : theme.shadows[4],
    },
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(1.5),
      borderRadius: 12,
    },
  },
  flowCardContent: {
    padding: theme.spacing(3),
    '&:last-child': {
      paddingBottom: theme.spacing(3),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
      '&:last-child': {
        paddingBottom: theme.spacing(2),
      },
    },
  },
  flowIcon: {
    width: 32,
    height: 32,
    backgroundColor: theme.palette.type === 'dark' ? 'rgba(0, 229, 255, 0.1)' : theme.palette.primary.main,
    color: theme.palette.type === 'dark' ? '#00E5FF' : theme.palette.primary.contrastText,
    borderRadius: 12,
    border: theme.palette.type === 'dark' ? '1px solid rgba(0, 229, 255, 0.3)' : 'none',
    boxShadow: theme.palette.type === 'dark' ? '0 0 10px rgba(0, 229, 255, 0.2)' : 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      width: 40,
      height: 40,
      marginRight: theme.spacing(1.5),
    },
  },
  flowName: {
    fontSize: '1rem',
    fontWeight: 400,
    fontFamily: "'Inter', 'Roboto', sans-serif !important",
    color: theme.palette.text.primary,
    lineHeight: 1.4,
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.9rem',
    },
  },
  flowActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(1.5),
    },
  },
  statusChip: {
    fontWeight: "bold",
    fontFamily: "'Inter', 'Roboto', sans-serif !important",
    borderRadius: 8,
    fontSize: "0.75rem",
    height: 24,
    width: 105,
    justifyContent: 'flex-start',
    '& .MuiChip-label': {
      width: '100%',
      textAlign: 'center'
    },
    '&.active': {
      backgroundColor: theme.palette.type === 'dark' ? 'rgba(0, 230, 118, 0.15)' : '#e8f5e8',
      color: theme.palette.type === 'dark' ? '#00e676' : '#2e7d32',
      border: `1px solid ${theme.palette.type === 'dark' ? '#00e676' : '#c8e6c9'}`,
      '& .MuiChip-icon': {
        color: theme.palette.type === 'dark' ? '#00e676' : '#2e7d32',
      }
    },
    '&.inactive': {
      backgroundColor: theme.palette.type === 'dark' ? 'rgba(255, 23, 68, 0.15)' : '#ffebee',
      color: theme.palette.type === 'dark' ? '#ff1744' : '#c62828',
      border: `1px solid ${theme.palette.type === 'dark' ? '#ff1744' : '#ffcdd2'}`,
      '& .MuiChip-icon': {
        color: theme.palette.type === 'dark' ? '#ff1744' : '#c62828',
      }
    }
  },
  actionButton: {
    minWidth: 40,
    width: 40,
    height: 40,
    borderRadius: 10,
    '& svg': {
      color: theme.palette.type === 'dark' ? '#FFFFFF' : theme.palette.text.secondary,
      filter: theme.palette.type === 'dark' ? 'drop-shadow(0 0 3px rgba(255,255,255,0.6))' : 'none',
      transition: 'all 0.3s ease',
    },
    '&:hover': {
      backgroundColor: theme.palette.type === 'dark' ? 'rgba(0, 229, 255, 0.1)' : theme.palette.action.hover,
    },
    '&:hover svg': {
      color: theme.palette.type === 'dark' ? '#00E5FF' : theme.palette.primary.main,
      filter: theme.palette.type === 'dark' ? 'drop-shadow(0 0 6px rgba(0,229,255,0.8))' : 'none',
    },
    [theme.breakpoints.down('sm')]: {
      minWidth: 36,
      width: 36,
      height: 36,
    },
  },
  menuButton: {
    minWidth: 40,
    width: 40,
    height: 40,
    borderRadius: 10,
    border: theme.palette.type === 'dark' ? '1px solid rgba(255, 255, 255, 0.2)' : `1px solid ${theme.palette.divider}`,
    '& svg': {
      color: theme.palette.type === 'dark' ? '#FFFFFF' : theme.palette.text.secondary,
      filter: theme.palette.type === 'dark' ? 'drop-shadow(0 0 3px rgba(255,255,255,0.6))' : 'none',
      transition: 'all 0.3s ease',
    },
    '&:hover': {
      backgroundColor: theme.palette.type === 'dark' ? 'rgba(0, 229, 255, 0.1)' : theme.palette.action.hover,
      borderColor: theme.palette.type === 'dark' ? '#00E5FF' : theme.palette.text.secondary,
    },
    '&:hover svg': {
      color: theme.palette.type === 'dark' ? '#00E5FF' : theme.palette.text.primary,
      filter: theme.palette.type === 'dark' ? 'drop-shadow(0 0 6px rgba(0,229,255,0.8))' : 'none',
    },
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(6),
    backgroundColor: theme.palette.background.paper,
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(4),
    },
  },
  emptyIcon: {
    fontSize: 64,
    color: theme.palette.text.disabled,
    marginBottom: theme.spacing(2),
  },
  emptyTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
  },
  emptyDescription: {
    color: theme.palette.text.secondary,
    fontSize: '0.95rem',
  },
  addButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderRadius: 12,
    padding: theme.spacing(1.5, 3),
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.9rem',
    boxShadow: theme.shadows[3],
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      boxShadow: theme.shadows[6],
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.2, 2.5),
    },
  },
  fab: {
    position: 'fixed',
    bottom: theme.spacing(3),
    right: theme.spacing(3),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    width: 64,
    height: 64,
    boxShadow: theme.shadows[6],
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      boxShadow: theme.shadows[12],
    },
    [theme.breakpoints.down('sm')]: {
      bottom: theme.spacing(2),
      right: theme.spacing(2),
      width: 56,
      height: 56,
    },
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
    backgroundColor: theme.palette.background.paper,
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
  },
  menu: {
    '& .MuiPaper-root': {
      borderRadius: 12,
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: theme.shadows[8],
      minWidth: 180,
      backgroundColor: theme.palette.background.paper,
    },
    '& .MuiMenuItem-root': {
      padding: theme.spacing(1.5, 2),
      fontSize: '0.9rem',
      color: theme.palette.text.primary,
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },
  },
}));

function HideOnScroll({ children }) {
  const trigger = useScrollTrigger();

  return (
    <Slide appear={false} direction="up" in={!trigger}>
      {children}
    </Slide>
  );
}

function FlowCard({ flow, whatsApps, onEdit, onDuplicate, onDelete, onNavigate, onToggleActive, onExport, classes }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Extraer keywords y conexiones
  const uniqueKeywordsMap = new Map();
  const connectionsIds = new Set();

  if (flow.phrase) {
    let parsedPhrases = [];
    try {
      parsedPhrases = typeof flow.phrase === 'string' ? JSON.parse(flow.phrase) : flow.phrase;
      if (!Array.isArray(parsedPhrases)) parsedPhrases = [parsedPhrases];
    } catch(e) {}
    parsedPhrases.forEach(p => {
      if (p && p.text) {
        const type = p.type || 'exact';
        const key = `${type}-${p.text}`;
        uniqueKeywordsMap.set(key, { text: p.text, type });
      }
    });
  } else if (flow.campaigns && Array.isArray(flow.campaigns)) {
    flow.campaigns.forEach(camp => {
      if (Array.isArray(camp.phrase)) {
        camp.phrase.forEach(p => {
          if (p && p.text) {
            const type = p.type || 'exact';
            const key = `${type}-${p.text}`;
            if (!uniqueKeywordsMap.has(key)) {
              uniqueKeywordsMap.set(key, { text: p.text, type });
            }
          }
        });
      }
    });
  }

  if (flow.whatsappIds !== null && flow.whatsappIds !== undefined) {
    let parsedIds = [];
    try {
      parsedIds = typeof flow.whatsappIds === 'string' ? JSON.parse(flow.whatsappIds) : flow.whatsappIds;
      if (!Array.isArray(parsedIds)) parsedIds = [parsedIds];
    } catch(e) {}
    parsedIds.forEach(id => connectionsIds.add(id));
  } else if (flow.campaigns && Array.isArray(flow.campaigns)) {
    flow.campaigns.forEach(camp => {
      let parsedIds = [];
      try {
        parsedIds = typeof camp.whatsappIds === 'string' ? JSON.parse(camp.whatsappIds) : camp.whatsappIds;
        if (!Array.isArray(parsedIds)) parsedIds = [parsedIds];
      } catch(e) {}
      parsedIds.forEach(id => connectionsIds.add(id));
    });
  }

  const uniqueKeywords = Array.from(uniqueKeywordsMap.values());
  const connections = [...connectionsIds].map(id => {
    return whatsApps?.find(w => w.id === id) || { id, name: `WhatsApp ${id}`, channel: 'whatsapp', color: null };
  });

  const isDarkMode = theme.mode === 'dark' || theme.palette.type === 'dark' || theme.palette.mode === 'dark';

  const getTypeLabel = (type) => {
    switch (type) {
      case 'partial': return i18n.t("campaignsPhrase.modal.partial") || 'Contiene';
      case 'not_contains': return i18n.t("campaignsPhrase.modal.not_contains") || 'No Contiene';
      case 'exact':
      default: return i18n.t("campaignsPhrase.modal.exact") || 'Exacto';
    }
  };

  const getChannelColor = (channel) => {
    switch (channel) {
      case "facebook":
      case "messenger":
        return isDarkMode ? "#4facfe" : "#1877f2";
      case "instagram":
        return "#e1306c";
      case "whatsapp_oficial":
        return "#25d366";
      case "telegram":
        return "#0088cc";
      case "whatsapp":
      default:
        return isDarkMode ? "#ffffff" : "#1f2937";
    }
  };

  const renderChannelIcon = (channel) => {
    const color = getChannelColor(channel);
    switch (channel) {
      case "facebook":
      case "messenger":
        return <Facebook style={{ color, fontSize: 14, marginLeft: '4px' }} />;
      case "instagram":
        return <Instagram style={{ color, fontSize: 14, marginLeft: '4px' }} />;
      case "telegram":
        return <Telegram style={{ color, fontSize: 14, marginLeft: '4px' }} />;
      case "whatsapp_oficial":
      case "whatsapp":
      default:
        return <WhatsApp style={{ color, fontSize: 14, marginLeft: '4px' }} />;
    }
  };

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAction = (action) => {
    handleMenuClose();
    // Usar setTimeout para garantir que o menu feche antes de executar a ação
    setTimeout(() => {
      action();
    }, 100);
  };

  const handleCardClick = (event) => {
    // Verifica se o clique foi em um botão de ação
    if (event.target.closest('button') || event.target.closest('[role="button"]')) {
      return;
    }
    onNavigate(flow.id);
  };

  const handleEditClick = (event) => {
    event.stopPropagation();
    onEdit();
  };

  const handleDuplicateClick = (event) => {
    event.stopPropagation();
    onDuplicate();
  };

  return (
    <Grid
      container
      onClick={handleCardClick}
className={classes.rowItem}
      alignItems="center"
    >
      {/* Coluna do Nome */}
      <Grid item xs={3}>
        <Stack
          justifyContent={"center"}
          height={"100%"}
        >
          <Stack direction={"row"} alignItems="center">
            <div className={classes.flowIcon}>
              <DevicesFold style={{ color: 'inherit', fontSize: 18 }} />
            </div>
            <Stack justifyContent={"center"} marginLeft={1}>
              <Typography variant="body2" style={{ fontWeight: 400, fontFamily: "'Inter', 'Roboto', sans-serif !important" }}>
                {flow.name || "Sin Nombre"}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Grid>

      {/* NOVA COLUNA: Palabras Claves */}
      <Grid item xs={3} style={{ paddingRight: "24px" }} align="center">
        <Stack justifyContent={"center"} alignItems="center" height={"100%"}>
          <Box display="flex" flexWrap="wrap" gap={0.5} style={{ maxWidth: "100%", overflow: "hidden" }}>
            {uniqueKeywords.length > 0 ? uniqueKeywords.map((kw, i) => (
              <Chip
                key={`kw-${i}`}
                size="small"
                label={`${kw.text} (${getTypeLabel(kw.type)})`}
                variant="outlined"
                style={{ 
                  fontFamily: "'Inter', 'Roboto', sans-serif !important",
                  color: isDarkMode ? '#64b5f6' : theme.palette.primary.main,
                  borderColor: isDarkMode ? 'rgba(100, 181, 246, 0.5)' : theme.palette.primary.main,
                  backgroundColor: isDarkMode ? 'rgba(100, 181, 246, 0.05)' : 'rgba(25, 118, 210, 0.05)',
                  maxWidth: "100%"
                }}
              />
            )) : (
              <Typography variant="body2" style={{ color: "inherit" }}>{i18n.t("flowBuilder.page.none") || "Ninguna"}</Typography>
            )}
          </Box>
        </Stack>
      </Grid>

      {/* NOVA COLUNA: Conexões WhatsApp */}
      <Grid item xs={2} align="center">
        <Stack justifyContent={"center"} height={"100%"}>
          <Box display="flex" flexWrap="wrap" gap={0.5} justifyContent="center" style={{ maxWidth: "100%", overflow: "hidden" }}>
            {connections.length > 0 ? connections.map((wa, i) => {
              const channelColor = getChannelColor(wa.channel);
              return (
                <Chip
                  key={`conn-${i}`}
                  size="small"
                  icon={renderChannelIcon(wa.channel)}
                  label={wa.name}
                  variant="outlined"
                  style={{ 
                    fontFamily: "'Inter', 'Roboto', sans-serif !important",
                    color: channelColor,
                    borderColor: channelColor,
                    backgroundColor: isDarkMode ? `${channelColor}1A` : `${channelColor}10`
                  }}
                />
              );
            }) : (
              <Typography variant="body2" style={{ color: "inherit" }}>{i18n.t("flowBuilder.page.none") || "Ninguna"}</Typography>
            )}
          </Box>
        </Stack>
      </Grid>

      {/* Coluna do Status */}
      <Grid item xs={2} align="center">
        <Stack justifyContent={"center"} height={"100%"}>
          <div
            onClick={(e) => { 
              e.preventDefault();
              e.stopPropagation(); 
              onToggleActive && onToggleActive(flow.id, flow.active); 
            }}
            style={{ display: 'inline-block' }}
          >
            <Chip
              size="small"
              label={flow.active ? i18n.t("campaignsPhrase.table.active") : i18n.t("campaignsPhrase.table.inactive")}
              className={`${classes.statusChip} ${flow.active ? 'active' : 'inactive'}`}
              icon={flow.active ? <PlayArrow /> : <Pause />}
              style={{ cursor: 'pointer', fontFamily: "'Inter', 'Roboto', sans-serif !important" }}
            />
          </div>
        </Stack>
      </Grid>

      {/* Coluna das Ações */}
      <Grid item xs={2} align="center">
        <Stack direction="row" spacing={0.5} justifyContent="center" height="100%" alignItems="center">
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onNavigate(flow.id); }}
            title={i18n.t("flowBuilder.page.openFlow") || "Abrir Canvas"}
          >
            <Build fontSize="small" style={{ color: "#00a8ff" }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); handleEditClick(e); }}
            title={i18n.t("common.edit") || "Editar Configuración"}
          >
            <Settings fontSize="small" style={{ color: "#fbc531" }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
            title="Duplicar Flujo"
          >
            <ContentCopy fontSize="small" style={{ color: "#4cd137" }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onExport(flow.id, flow.name); }}
            title={i18n.t("flowBuilder.page.export") || "Exportar"}
          >
            <svg style={{width: 20, height: 20, color: "#9c88ff"}} viewBox="0 0 24 24">
               <path fill="currentColor" d="M19,9h-4V3H9v6H5l7,7L19,9z M5,18v2h14v-2H5z" />
            </svg>
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            title={i18n.t("common.delete") || "Eliminar"}
          >
            <DeleteOutlineIcon style={{ color: "#e84118" }} />
          </IconButton>
        </Stack>
      </Grid>
    </Grid>
  );
}

const FlowBuilder = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [contacts, dispatch] = useReducer(reducer, []);
  
  const [collapsedBrands, setCollapsedBrands] = useState({});
  const [collapsedKanbans, setCollapsedKanbans] = useState({});

  const toggleBrand = (id) => {
    setCollapsedBrands((prev) => ({ ...prev, [id]: prev[id] === false ? true : false }));
  };

  const toggleKanban = (id) => {
    setCollapsedKanbans((prev) => ({ ...prev, [id]: prev[id] === false ? true : false }));
  };

  const [webhooks, setWebhooks] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [selectedWebhookName, setSelectedWebhookName] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [contactTicket, setContactTicket] = useState({});
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmDuplicateOpen, setConfirmDuplicateOpen] = useState(false);
  const [importInputRef, setImportInputRef] = useState(null);

  const [hasMore, setHasMore] = useState(false);
  const [reloadData, setReloadData] = useState(false);
  const { user, socket } = useContext(AuthContext);
  const { whatsApps } = useContext(WhatsAppsContext);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("/flowbuilder");
          setWebhooks(data.flows);
          dispatch({ type: "LOAD_CONTACTS", payload: data.flows });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
          setLoading(false);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, reloadData]);

  useEffect(() => {
    const companyId = user.companyId;

    const onContact = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CONTACTS", payload: data.contact });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACT", payload: +data.contactId });
      }
    };

    socket.on(`company-${companyId}-contact`, onContact);

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(false);
  };

  const handleCloseOrOpenTicket = (ticket) => {
    setNewTicketModalOpen(false);
    if (ticket !== undefined && ticket.uuid !== undefined) {
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  const handleEditContact = (contact) => {
    setSelectedContactId(contact.id);
    setSelectedWebhookName(contact.name);
    setContactModalOpen(true);
  };

  const handleToggleFlowActive = async (flowId, currentActive) => {
    try {
      const newActive = !currentActive;
      const { data } = await api.put(`/flowbuilder/${flowId}/toggle-active`, {
        active: newActive
      });

      // Atualizar o fluxo no estado
      dispatch({
        type: "UPDATE_CONTACTS",
        payload: { ...data.data, id: flowId }
      });

      // Atualizar também no estado webhooks
      setWebhooks((prev) =>
        prev.map((flow) =>
          flow.id === flowId ? { ...flow, active: newActive } : flow
        )
      );

      toast.success(data.message || (newActive ? i18n.t("flowBuilder.toasts.activated") : i18n.t("flowBuilder.toasts.deactivated")));
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteWebhook = async (webhookId) => {
    try {
      await api.delete(`/flowbuilder/${webhookId}`);
      setDeletingContact(null);
      setConfirmOpen(false);
      setReloadData((old) => !old);
      toast.success(i18n.t("flowBuilder.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
  };

  const handleDuplicateFlow = async (flowId) => {
    try {
      await api.post(`/flowbuilder/duplicate`, { flowId: flowId });
      setDeletingContact(null);
      setConfirmDuplicateOpen(false);
      setReloadData((old) => !old);
      toast.success(i18n.t("flowBuilder.toasts.duplicated"));
    } catch (err) {
      toastError(err);
    }
  };

  const handleExportFlow = async (flowId, name) => {
    try {
      const response = await api.get(`/flowbuilder/${flowId}/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${name.replace(/[^a-z0-9-_]/gi, '_')}.zip`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toastError(err);
    }
  };

  const handleImportClick = () => {
    if (importInputRef) {
      importInputRef.click();
    }
  };

  const handleImportChange = async (e) => {
    const input = e.target;
    const file = input.files && input.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.zip')) {
      toast.error(i18n.t("flowBuilder.toasts.selectZip"));
      input.value = null;
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);

      await api.post('/flowbuilder/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(i18n.t("flowBuilder.toasts.imported"));
      setReloadData((old) => !old);
    } catch (err) {
      toastError(err);
    } finally {
      input.value = null;
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const filteredWebhooks = webhooks.filter(webhook =>
    webhook.name.toLowerCase().includes(searchParam.toLowerCase())
  );

  return (
    <MainContainer className={classes.mainContainer}>
      {/* Modais - mantendo todos originais */}
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        initialContact={contactTicket}
        onClose={(ticket) => {
          handleCloseOrOpenTicket(ticket);
        }}
      />

      <FlowSetupModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        aria-labelledby="form-dialog-title"
        flowId={selectedContactId}
        nameWebhook={selectedWebhookName}
        onSave={() => setReloadData((old) => !old)}
      />

      <ConfirmationModal
        title={
          deletingContact
            ? `${i18n.t("flowBuilder.modals.deleteTitle")} "${deletingContact.name}"?`
            : `${i18n.t("contacts.confirmationModal.importTitlte")}`
        }
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={() => {
          if (deletingContact) {
            handleDeleteWebhook(deletingContact.id);
          }
        }}
      >
        {deletingContact
          ? i18n.t("flowBuilder.modals.deleteMessage")
          : `${i18n.t("contacts.confirmationModal.importMessage")}`}
      </ConfirmationModal>

      <ConfirmationModal
        title={
          deletingContact
            ? `${i18n.t("flowBuilder.modals.duplicateTitle")} "${deletingContact.name}"?`
            : `${i18n.t("contacts.confirmationModal.importTitlte")}`
        }
        open={confirmDuplicateOpen}
        onClose={setConfirmDuplicateOpen}
        onConfirm={() => {
          if (deletingContact) {
            handleDuplicateFlow(deletingContact.id);
          }
        }}
      >
        {deletingContact
          ? i18n.t("flowBuilder.modals.duplicateMessage")
          : `${i18n.t("contacts.confirmationModal.importMessage")}`}
      </ConfirmationModal>

      <Paper
        className={classes.mainPaper}
        variant="outlined"
      >
        <MainHeader>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <PageHelpGuide
              helpData={FLOW_BUILDER_HELP_DATA}
              tooltipText="Guía y Ayuda de Flow Builder (Haz clic para ver)"
            />
            <Title>{i18n.t("flowBuilder.page.title")} ({webhooks.length})</Title>
          </div>
          <MainHeaderButtonsWrapper>
            <input
              type="file"
              accept=".zip,application/zip"
              ref={(ref) => setImportInputRef(ref)}
              style={{ display: 'none' }}
              onChange={handleImportChange}
            />
            <TextField
              placeholder={i18n.t("flowBuilder.page.searchPlaceholder")}
              type="search"
              size="small"
              variant="outlined"
              value={searchParam}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "gray" }} />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="outlined"
              size="small"
              className={classes.secondaryButton}
              onClick={handleImportClick}
              startIcon={<CloudUploadIcon style={{ fontSize: 18 }} />}
            >
              {i18n.t("flowBuilder.page.import")}
            </Button>
            <Button
              variant="outlined"
              size="small"
              className={classes.applyButton}
              onClick={handleOpenContactModal}
              startIcon={<AddIcon style={{ fontSize: 18 }} />}
            >
              {i18n.t("flowBuilder.page.newFlow")}
            </Button>
          </MainHeaderButtonsWrapper>
        </MainHeader>
        {loading && !webhooks.length ? (
          <div className={classes.loadingContainer}>
            <CircularProgress style={{ color: theme.palette.primary.main }} />
          </div>
        ) : filteredWebhooks.length === 0 ? (
          <div className={classes.emptyState}>
            <DevicesFold className={classes.emptyIcon} />
            <Typography className={classes.emptyTitle}>
              {searchParam ? i18n.t("flowBuilder.page.emptySearch") : i18n.t("flowBuilder.page.emptyFlows")}
            </Typography>
            <Typography className={classes.emptyDescription}>
              {searchParam
                ? i18n.t("flowBuilder.page.emptySearchDesc")
                : i18n.t("flowBuilder.page.emptyFlowsDesc")
              }
            </Typography>

            {!searchParam && (
              <Button
                className={classes.addButton}
                onClick={handleOpenContactModal}
                startIcon={<AddCircle />}
                style={{ marginTop: 24 }}
              >
                {i18n.t("flowBuilder.page.createFirstFlow")}
              </Button>
            )}
          </div>
        ) : (
          <div className={classes.scrollArea} onScroll={handleScroll}>
            {(() => {
              const groupedFlows = {};
              filteredWebhooks.forEach(flow => {
                const brandId = flow.brandId || "unbranded";
                const brandName = flow.brand?.name || "Sin Marca";
                const kanbanId = flow.kanbanId || "nokanban";
                const kanbanName = flow.kanban?.name || "Sin Embudo";

                if (!groupedFlows[brandId]) {
                  groupedFlows[brandId] = { name: brandName, kanbans: {} };
                }
                if (!groupedFlows[brandId].kanbans[kanbanId]) {
                  groupedFlows[brandId].kanbans[kanbanId] = { name: kanbanName, flows: [] };
                }
                groupedFlows[brandId].kanbans[kanbanId].flows.push(flow);
              });

              return Object.keys(groupedFlows).map(brandId => (
                <Paper key={brandId} style={{ padding: '8px 16px', border: `1px solid ${theme.palette.divider}`, backgroundColor: theme.palette.background.paper, marginBottom: 8 }} elevation={0}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: collapsedBrands[brandId] !== false ? 0 : 8 }}>
                    <div onClick={() => toggleBrand(brandId)} style={{ display: 'flex', alignItems: 'center', flex: 1, cursor: 'pointer', userSelect: 'none' }}>
                      {collapsedBrands[brandId] !== false ? <ChevronRight style={{ marginRight: 4, color: theme.palette.text.secondary }} /> : <ExpandMore style={{ marginRight: 4, color: theme.palette.text.secondary }} />}
                      <Typography variant="subtitle1" style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Business style={{ color: '#3498db' }} /> {groupedFlows[brandId].name}
                      </Typography>
                    </div>
                  </div>
                  <Collapse in={collapsedBrands[brandId] === false}>
                    <Divider style={{ marginBottom: 8 }} />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {Object.keys(groupedFlows[brandId].kanbans).map(kanbanId => (
                        <div key={kanbanId} style={{ display: 'flex', flexDirection: 'column', background: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)', borderRadius: 4, border: `1px solid ${theme.palette.divider}` }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 16px' }}>
                            <div onClick={() => toggleKanban(kanbanId)} style={{ display: 'flex', alignItems: 'center', flex: 1, cursor: 'pointer', userSelect: 'none' }}>
                              {collapsedKanbans[kanbanId] !== false ? <ChevronRight style={{ marginRight: 8, color: theme.palette.text.secondary, fontSize: '1.2rem' }} /> : <ExpandMore style={{ marginRight: 8, color: theme.palette.text.secondary, fontSize: '1.2rem' }} />}
                              <Typography variant="body2" style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                                <FilterList style={{ color: '#e67e22', marginRight: 8, fontSize: '1.2rem' }} /> {groupedFlows[brandId].kanbans[kanbanId].name}
                              </Typography>
                            </div>
                          </div>
                          <Collapse in={collapsedKanbans[kanbanId] === false}>
                            <div style={{ paddingLeft: 24, paddingRight: 16, paddingBottom: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                              <Grid container className={classes.tableHeader} style={{ padding: "8px", marginTop: "8px" }}>
                                <Grid item xs={3} align="center"><Typography variant="subtitle2">{i18n.t("flowBuilder.table.flowName")}</Typography></Grid>
                                <Grid item xs={3} align="center"><Typography variant="subtitle2">{i18n.t("flowBuilder.table.keywords")}</Typography></Grid>
                                <Grid item xs={2} align="center"><Typography variant="subtitle2">{i18n.t("flowBuilder.table.connections")}</Typography></Grid>
                                <Grid item xs={2} align="center"><Typography variant="subtitle2">{i18n.t("flowBuilder.table.status")}</Typography></Grid>
                                <Grid item xs={2} align="center"><Typography variant="subtitle2">{i18n.t("flowBuilder.table.actions")}</Typography></Grid>
                              </Grid>
                              {groupedFlows[brandId].kanbans[kanbanId].flows.map(flow => (
                                <FlowCard
                                  key={flow.id}
                                  flow={flow}
                                  whatsApps={whatsApps}
                                  classes={classes}
                                  onEdit={() => handleEditContact(flow)}
                                  onDuplicate={() => { setDeletingContact(flow); setConfirmDuplicateOpen(true); }}
                                  onDelete={() => { setDeletingContact(flow); setConfirmOpen(true); }}
                                  onNavigate={(id) => history.push(`/flowbuilder/${id}`)}
                                  onToggleActive={(id, active) => handleToggleFlowActive(id, active)}
                                  onExport={handleExportFlow}
                                />
                              ))}
                            </div>
                          </Collapse>
                        </div>
                      ))}
                    </div>
                  </Collapse>
                </Paper>
              ));
            })()}

            {loading && webhooks.length > 0 && (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress size={24} style={{ color: theme.palette.primary.main }} />
              </Box>
            )}
          </div>
        )}
      </Paper>

      {isMobile && (
        <HideOnScroll>
          <Fab
            className={classes.fab}
            onClick={handleOpenContactModal}
            aria-label={i18n.t("flowBuilder.page.newFlow")}
          >
            <AddCircle />
          </Fab>
        </HideOnScroll>
      )}
    </MainContainer>
  );
};

export default FlowBuilder;
